import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Send, User, Mail, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  createSectionTracker,
  trackEvent,
  calculateSemaforo,
  submitLead,
  type SemaforoColor,
} from '../../services/funnelTracker';

// ============================================
// TYPES
// ============================================

interface FormData {
  name: string;
  email: string;
  dolor: string;
  capacidad: string[];
  timing: string;
  inversion: string;
}

interface QualificationFormProps {
  onComplete: (result: {
    semaforo: {
      dolor: SemaforoColor;
      capacidad: SemaforoColor;
      timing: SemaforoColor;
      inversion: SemaforoColor;
      score: number;
    };
    leadId?: number;
  }) => void;
}

// ============================================
// STEP OPTIONS
// ============================================

const dolorOptions = [
  { value: 'trabajo_datos_crecer', label: 'Trabajo en datos y quiero crecer' },
  { value: 'trabajo_it_cambiar', label: 'Trabajo en IT y quiero pasar a datos' },
  { value: 'estudiando', label: 'Estoy estudiando por mi cuenta' },
  { value: 'sin_experiencia', label: 'Recién empiezo, no tengo experiencia' },
];

const capacidadOptions = [
  { value: 'SQL', label: 'SQL' },
  { value: 'Python', label: 'Python' },
  { value: 'Excel', label: 'Excel' },
  { value: 'Databricks', label: 'Databricks' },
  { value: 'Power BI', label: 'Power BI' },
  { value: 'Ninguna', label: 'Ninguna' },
];

const timingOptions = [
  { value: 'esta_semana', label: 'Esta semana' },
  { value: 'este_mes', label: 'Este mes' },
  { value: 'en_2_3_meses', label: 'En 2-3 meses' },
  { value: 'no_seguro', label: 'No estoy seguro' },
];

const inversionOptions = [
  { value: 'si_tengo', label: 'Sí, cuento con $500 USD o más' },
  { value: 'puedo_conseguirlo', label: 'No lo tengo hoy, pero puedo juntarlo pronto' },
  { value: 'tendria_que_evaluar', label: 'No cuento con ese presupuesto ahora' },
];

// ============================================
// COMPONENT
// ============================================

export default function QualificationForm({ onComplete }: QualificationFormProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [currentStep, setCurrentStep] = useState(0); // 0 = name/email, 1-4 = BANT steps
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formTracked, setFormTracked] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    dolor: '',
    capacidad: [],
    timing: '',
    inversion: '',
  });

  // Section tracking
  useEffect(() => {
    const tracker = createSectionTracker('form_section');
    if (sectionRef.current) tracker.ref(sectionRef.current);
    return () => tracker.cleanup();
  }, []);

  // Track form_started when form section is first interacted with
  useEffect(() => {
    if (currentStep > 0 && !formTracked) {
      trackEvent('form_started');
      setFormTracked(true);
    }
  }, [currentStep, formTracked]);

  // Track each step completion
  const goToStep = (step: number) => {
    if (step > currentStep) {
      trackEvent(`form_step_${currentStep}`, {
        step: currentStep,
        data: currentStep === 0 ? { has_email: !!formData.email } : getStepData(currentStep),
      });
    }
    setCurrentStep(step);
  };

  const getStepData = (step: number): Record<string, unknown> => {
    switch (step) {
      case 1: return { dolor: formData.dolor };
      case 2: return { capacidad: formData.capacidad };
      case 3: return { timing: formData.timing };
      case 4: return { inversion: formData.inversion };
      default: return {};
    }
  };

  const isStepValid = (): boolean => {
    switch (currentStep) {
      case 0: return formData.name.trim().length >= 2 && /\S+@\S+\.\S+/.test(formData.email);
      case 1: return formData.dolor !== '';
      case 2: return formData.capacidad.length > 0;
      case 3: return formData.timing !== '';
      case 4: return formData.inversion !== '';
      default: return false;
    }
  };

  const handleSubmit = async () => {
    if (!isStepValid()) return;
    setIsSubmitting(true);

    trackEvent('form_step_4', { inversion: formData.inversion });

    const semaforo = calculateSemaforo(formData);

    const result = await submitLead({
      name: formData.name,
      email: formData.email,
      dolor: formData.dolor,
      capacidad: formData.capacidad,
      timing: formData.timing,
      inversion: formData.inversion,
      semaforo_dolor: semaforo.dolor,
      semaforo_capacidad: semaforo.capacidad,
      semaforo_timing: semaforo.timing,
      semaforo_inversion: semaforo.inversion,
      semaforo_score: semaforo.score,
    });

    setIsSubmitting(false);
    onComplete({ semaforo, leadId: result.lead_id });
  };

  const handleCapacidadToggle = (value: string) => {
    setFormData((prev) => {
      // If selecting "Ninguna", deselect everything else
      if (value === 'Ninguna') {
        return { ...prev, capacidad: ['Ninguna'] };
      }
      // If selecting something else, remove "Ninguna"
      const withoutNone = prev.capacidad.filter((v) => v !== 'Ninguna');
      const isSelected = withoutNone.includes(value);
      return {
        ...prev,
        capacidad: isSelected
          ? withoutNone.filter((v) => v !== value)
          : [...withoutNone, value],
      };
    });
  };

  const totalSteps = 5; // 0=info, 1=dolor, 2=capacidad, 3=timing, 4=inversion
  const progress = ((currentStep + 1) / totalSteps) * 100;

  return (
    <section ref={sectionRef} id="qualification-form" className="py-16 sm:py-24 px-4 sm:px-6">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          {/* Header */}
          <div className="text-center mb-8 sm:mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              ¿Es para vos?
            </h2>
            <p className="text-gray-400 text-base sm:text-lg">
              Respondé estas 4 preguntas rápidas (30 segundos) y te digo si te puedo ayudar.
            </p>
          </div>

          {/* Form card */}
          <div className="bg-gradient-to-br from-blue-600/20 via-purple-600/20 to-blue-700/20 border border-blue-500/30 rounded-3xl p-6 sm:p-10 shadow-2xl">
            {/* Progress bar */}
            <div className="mb-8">
              <div className="flex justify-between text-xs text-gray-400 mb-2">
                <span>Paso {currentStep + 1} de {totalSteps}</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.4 }}
                />
              </div>
            </div>

            {/* Steps */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Step 0: Name & Email */}
                {currentStep === 0 && (
                  <div className="space-y-5">
                    <h3 className="text-xl font-bold text-white mb-2">
                      Primero, ¿cómo te llamo?
                    </h3>
                    <div>
                      <label className="block text-sm text-gray-300 mb-2">Tu nombre</label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Ej: Luciano"
                          className="w-full h-12 pl-11 pr-4 rounded-xl bg-gray-900 border border-gray-700 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-2">Tu email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="tu@email.com"
                          className="w-full h-12 pl-11 pr-4 rounded-xl bg-gray-900 border border-gray-700 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 1: Dolor */}
                {currentStep === 1 && (
                  <div>
                    <h3 className="text-xl font-bold text-white mb-5">
                      ¿Cuál es tu situación hoy?
                    </h3>
                    <div className="space-y-3">
                      {dolorOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setFormData({ ...formData, dolor: option.value })}
                          className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                            formData.dolor === option.value
                              ? 'border-green-500 bg-green-500/10 text-white'
                              : 'border-gray-700 bg-gray-900/50 text-gray-300 hover:border-gray-500'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 2: Capacidad */}
                {currentStep === 2 && (
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">
                      ¿Qué herramientas manejás?
                    </h3>
                    <p className="text-gray-400 text-sm mb-5">Podés elegir más de una</p>
                    <div className="grid grid-cols-2 gap-3">
                      {capacidadOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => handleCapacidadToggle(option.value)}
                          className={`text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                            formData.capacidad.includes(option.value)
                              ? 'border-green-500 bg-green-500/10 text-white'
                              : 'border-gray-700 bg-gray-900/50 text-gray-300 hover:border-gray-500'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 3: Timing */}
                {currentStep === 3 && (
                  <div>
                    <h3 className="text-xl font-bold text-white mb-5">
                      ¿Cuándo querés arrancar?
                    </h3>
                    <div className="space-y-3">
                      {timingOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setFormData({ ...formData, timing: option.value })}
                          className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                            formData.timing === option.value
                              ? 'border-green-500 bg-green-500/10 text-white'
                              : 'border-gray-700 bg-gray-900/50 text-gray-300 hover:border-gray-500'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 4: Inversion */}
                {currentStep === 4 && (
                  <div>
                    <h3 className="text-xl font-bold text-white mb-3">
                      Los planes inician en $500 USD. ¿Contás con ese presupuesto?
                    </h3>
                    <p className="text-gray-400 text-sm mb-5">Sé honesto, esto me ayuda a recomendarte el mejor camino.</p>
                    <div className="space-y-3">
                      {inversionOptions.map((option) => (
                        <button
                          key={option.value}
                          onClick={() => setFormData({ ...formData, inversion: option.value })}
                          className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 ${
                            formData.inversion === option.value
                              ? 'border-green-500 bg-green-500/10 text-white'
                              : 'border-gray-700 bg-gray-900/50 text-gray-300 hover:border-gray-500'
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex justify-between items-center mt-8">
              {currentStep > 0 ? (
                <button
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Anterior
                </button>
              ) : (
                <div />
              )}

              {currentStep < 4 ? (
                <Button
                  onClick={() => goToStep(currentStep + 1)}
                  disabled={!isStepValid()}
                  className="bg-green-600 hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-6 py-3 rounded-full transition-all duration-300"
                >
                  Siguiente
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={!isStepValid() || isSubmitting}
                  className="bg-green-600 hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-6 py-3 rounded-full transition-all duration-300"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 w-4 h-4" />
                      Enviar mi aplicación
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
